import { Middleware, PayloadAction } from '@reduxjs/toolkit'

import {
  defaultSearchErrorTagManagerEvent,
  errorAddTrackEvent,
} from 'src/Helpers/TagManager'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { EventNameEnum } from 'src/Helpers/TagManager/tagManagerEnums'
import { getAllCampaignsCount, Metadata } from 'src/Redux/metadataApi'

import rootReducer from '../root-reducer'
import { RootState } from '../index'
import { ErrorCode } from '../types'

type Reducer = ReturnType<typeof rootReducer>

type MetaType = {
  requestStatus: 'pending' | 'fulfilled' | 'rejected'
}

type PayloadType = {
  code: number
  message: string
}

const spotifySearchTrackActions = [
  'getSpotifyTracks',
  'checkTrackToCreateCampaign',
]

const eventMapCodes: Record<number, EventNameEnum> = {
  [ErrorCode.Conflict]: EventNameEnum.ErrorSameTrack,
  [ErrorCode.Unauthorized]: EventNameEnum.ErrorAccessToken,
}

const getMetadataFromCache = (state: RootState): Metadata | undefined => {
  const { queries } = state.api
  const metadataQuery = Object.entries(queries).find(([key]) =>
    key.startsWith('metadata'),
  )
  return metadataQuery?.[1]?.data as Metadata | undefined
}

export const sendSpotifyTrackEvents: Middleware<unknown, Reducer> =
  (store) => (next) => (action: unknown) => {
    const typedAction = action as PayloadAction<PayloadType, string, MetaType>
    const state = store.getState() as RootState
    const { authentication } = state
    const { user } = authentication.user
    const metadata = getMetadataFromCache(state)
    const amountOfCampaigns = getAllCampaignsCount(metadata)

    const userId = user?.id
    const isFirstCampaign = user?.is_first_campaign
    const campaignNumber = amountOfCampaigns + 1

    const isSpotifySearchTrackAction = (actionType: string): boolean =>
      spotifySearchTrackActions.some((searchTrackActions) =>
        actionType.includes(searchTrackActions),
      )

    const isRejectedResponse =
      typedAction.meta?.requestStatus === 'rejected' &&
      isSpotifySearchTrackAction(typedAction.type)

    if (isRejectedResponse) {
      const eventName = eventMapCodes[typedAction.payload.code]

      eventName
        ? defaultSearchErrorTagManagerEvent({
            userId,
            eventName,
            campaignPlatform: CampaignPlatformTypeEnum.SPOTIFY,
          })
        : errorAddTrackEvent({
            userId,
            isFirstCampaign,
            campaignNumber,
            campaignPlatform: CampaignPlatformTypeEnum.SPOTIFY,
          })
    }

    return next(action)
  }
